// Pure reads over the trip that the compose screen draws from: totals,
// completeness, the rows of the timeline and the hotels of its programs. No
// React, no store — `days` and the two product maps come in as arguments.

import { getDayExperience } from '@koob/trip-designer';

const ACCOMMODATION_FREE_TYPES = ['noAccomodation', 'nightTransfer'];

export function isTripComplete(days, experiencesMap, tripFilters) {
  return (
    days?.length >= tripFilters.tripDuration &&
    days?.every(day => day?.experienceId) &&
    days?.every(day => {
      const experience = getDayExperience(day, experiencesMap);
      const experienceDayIdx = (day?.experienceDayIndex ?? 1) - 1;

      return (
        day?.hotelId ||
        day.hotelBookedOnMyOwn === true ||
        (experience?.programs &&
          experience?.programs[experienceDayIdx] &&
          ACCOMMODATION_FREE_TYPES.includes(
            experience?.programs[experienceDayIdx]?.accomodationType,
          ))
      );
    })
  );
}

export function getTotalTripPrice(days, experiencesMap) {
  let price = 0;
  const currenciesSet = new Set();
  const seenExperiences = new Set();
  const seenHotelGroups = new Set();

  for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
    const day = days[dayIndex];
    const experience = getDayExperience(day, experiencesMap);
    const totalExperience =
      day?.experiencePricing ?? experience?.tripPrice ?? experience?.price;

    // Add experience price only once per experience (on first day)
    if (
      experience &&
      totalExperience?.price !== undefined &&
      (!day?.experienceDayIndex || day.experienceDayIndex === 1) &&
      !seenExperiences.has(day.experienceId)
    ) {
      price += Math.round(totalExperience?.price ?? 0);
      if (totalExperience?.currency) {
        currenciesSet.add(totalExperience.currency);
      }
      seenExperiences.add(day.experienceId);
    }

    // Add hotel price only if:
    // 1. Not an included hotel
    // 2. Has pricing
    // 3. This is the first occurrence of this hotel group
    if (
      day?.hotelPricing?.price &&
      day.hotelId &&
      !day.hotelIsIncluded &&
      !seenHotelGroups.has(day.hotelId + '_' + dayIndex)
    ) {
      // Mark this hotel group as seen
      for (let i = dayIndex; i < days.length; i++) {
        const currentDay = days[i];
        if (currentDay.hotelId === day.hotelId && !currentDay.hotelIsIncluded) {
          seenHotelGroups.add(currentDay.hotelId + '_' + i);
        } else {
          break;
        }
      }
      price += Math.round(day.hotelPricing.price ?? 0);
      if (day.hotelPricing.currency) {
        currenciesSet.add(day.hotelPricing.currency);
      }
    }
  }

  const currencies = Array.from(currenciesSet);
  return { totalPrice: price, currency: currencies[0] };
}

// One block per rendered row: an experience frame swallows the days it
// covers, a day carrying no experience stands on its own, and a removal being
// offered back holds the days its experience covered together (AC09).
export function buildComposeBlocks({
  experienceFrames,
  dayCount,
  pendingRemoval,
}) {
  const frameByStartDay = new Map(
    experienceFrames.map(frame => [frame.startDayIndex, frame]),
  );
  const blocks = [];

  for (let dayIndex = 0; dayIndex < dayCount; dayIndex += 1) {
    const frame = frameByStartDay.get(dayIndex);

    if (frame && frame.dayIndices.length > 0) {
      blocks.push({ frame, dayIndices: frame.dayIndices });
      dayIndex = frame.dayIndices[frame.dayIndices.length - 1];
    } else if (pendingRemoval?.startDayIndex === dayIndex) {
      // The freed days keep the card their experience had, so the undo offer
      // takes the place of the header that carried the removal cross. The
      // walk stops short of anything composed since the removal.
      const removalDayIndices = [];
      const lastDayIndex = Math.min(
        dayIndex + (pendingRemoval.dayCount ?? 1),
        dayCount,
      );

      for (let index = dayIndex; index < lastDayIndex; index += 1) {
        if (index > dayIndex && frameByStartDay.has(index)) {
          break;
        }

        removalDayIndices.push(index);
      }

      blocks.push({
        frame: null,
        removal: pendingRemoval,
        dayIndices: removalDayIndices,
      });
      dayIndex = removalDayIndices[removalDayIndices.length - 1];
    } else {
      blocks.push({ frame: null, dayIndices: [dayIndex] });
    }
  }

  return blocks;
}

// Hotels the trip's programs suggest, taken from the hotels the list loaded.
export function getSuggestedHotels(days, experiencesMap, hotels) {
  const results = [];

  days
    ?.filter(d => {
      const exp = getDayExperience(d, experiencesMap);
      return exp?.programs?.length > 0;
    })
    ?.map(day => {
      const experience = getDayExperience(day, experiencesMap);
      experience?.programs
        ?.map(program => {
          if (
            program.accomodationHotel &&
            !results.some(p => p.id === program.accomodationHotel?.id)
          ) {
            const matchedAccomodationHotel = hotels?.find(
              hotel => hotel?.id === program.accomodationHotel?.id,
            );
            if (matchedAccomodationHotel) {
              results.push(matchedAccomodationHotel);
            }
          }
          program?.alternativeHotels?.map(alternative => {
            if (!results.some(p => p.id === alternative.id)) {
              const matchedAlternativeHotel = hotels?.find(
                hotel => hotel?.id === alternative?.id,
              );
              if (matchedAlternativeHotel) {
                results.push(matchedAlternativeHotel);
              }
            }
          });
        })
        .filter(Boolean);
    })
    .filter(Boolean);

  return results;
}
